import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuthStore } from '../stores/authStore';
import DashboardLayout from '../components/layout/DashboardLayout';
import PricingTable from '../components/PricingTable';
import api from '../lib/axios';
import { CreditCard, AlertCircle, CheckCircle } from 'lucide-react';

export default function Billing() {
  const { user } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleManageBilling = async () => {
    setIsLoading(true);
    setMessage(null);

    try {
      const response = await api.post('/api/billing/portal', {
        return_url: `${window.location.origin}/billing`,
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Failed to open billing portal',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const currentPlan = user?.plan || 'free';
  const hasPaidPlan = currentPlan !== 'free';

  return (
    <>
      <Helmet><meta name="robots" content="noindex,nofollow" /></Helmet>
    <DashboardLayout>
      <div>
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Billing</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Manage your subscription and billing information
          </p>
        </div>

        {message && (
          <div
            className={`mb-6 p-4 rounded-lg flex items-center ${
              message.type === 'success'
                ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-400'
                : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-400'
            }`}
          >
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5 mr-2" />
            ) : (
              <AlertCircle className="w-5 h-5 mr-2" />
            )}
            {message.text}
          </div>
        )}

        {/* Current Plan */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-brand-100 dark:bg-brand-900/20 rounded-lg flex items-center justify-center mr-4">
                <CreditCard className="w-6 h-6 text-brand-600 dark:text-brand-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Current Plan
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mt-1 capitalize">
                  {currentPlan} Plan
                </p>
              </div>
            </div>
            {hasPaidPlan && (
              <button
                onClick={handleManageBilling}
                disabled={isLoading}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Loading...' : 'Manage Subscription'}
              </button>
            )}
          </div>

          {hasPaidPlan && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Status:</span>
                <span className="px-2 py-1 rounded text-xs font-medium capitalize bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400">
                  Active
                </span>
              </div>
              {user?.subscription_ends_at && (
                <div className="flex items-center justify-between text-sm mt-2">
                  <span className="text-gray-600 dark:text-gray-400">Current period ends:</span>
                  <span className="text-gray-900 dark:text-white">
                    {new Date(user.subscription_ends_at).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Pricing Plans */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Available Plans
          </h2>
          <PricingTable />
        </div>
      </div>
    </DashboardLayout>
    </>
  );
}
